import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, MapPin, Heart, ShieldCheck, Briefcase } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { rupiah, VEHICLE_LABELS } from "../../lib/format";
import { Button } from "../ui/button";

export default function InspectorCard({ insp, vehicleType }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toggleFav = async (e) => {
    e.stopPropagation();
    try {
      if (insp.is_favorite) await api.delete(`/users/me/favorites/${insp.user_id}`); else await api.post(`/users/me/favorites/${insp.user_id}`);
      toast.success(insp.is_favorite ? "Dihapus dari favorit" : "Ditambahkan ke favorit", { duration: 1200 });
      qc.invalidateQueries({ queryKey: ["inspectors"] }); qc.invalidateQueries({ queryKey: ["favorites"] }); qc.invalidateQueries({ queryKey: ["inspector", insp.user_id] }); qc.invalidateQueries({ queryKey: ["user-dashboard"] });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const price = vehicleType && insp.prices?.[vehicleType] ? insp.prices[vehicleType] : Math.min(...Object.values(insp.prices || { x: 0 }));
  return (
    <div onClick={() => navigate(`/pengguna/inspector/${insp.user_id}`)} data-testid={`inspector-card-${insp.user_id}`}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group">
      <div className="flex items-start gap-3">
        <div className="relative">
          <img src={insp.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(insp.name)}&background=2563eb&color=fff`} alt={insp.name} className="w-14 h-14 rounded-2xl object-cover" />
          <span className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${insp.is_online ? "bg-emerald-500" : "bg-slate-300"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-extrabold text-slate-900 truncate">{insp.name}</p>
            <ShieldCheck size={15} className="text-blue-600 shrink-0" fill="#dbeafe" />
          </div>
          <p className="text-[11px] text-amber-500 font-bold flex items-center gap-1 mt-0.5"><Star size={11} fill="#f59e0b" /> {insp.rating_avg || "Baru"} <span className="text-slate-400 font-medium">({insp.rating_count} ulasan)</span></p>
          <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={10} /> {insp.city}</p>
        </div>
        <button onClick={toggleFav} data-testid={`fav-btn-${insp.user_id}`} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
          <Heart size={18} className={insp.is_favorite ? "text-red-500" : "text-slate-300 group-hover:text-red-300"} fill={insp.is_favorite ? "#ef4444" : "none"} />
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-3 line-clamp-2 min-h-[32px]">{insp.bio}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {insp.vehicle_types.map((v) => <span key={v} className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${v === vehicleType ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>{VEHICLE_LABELS[v]}</span>)}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-50">
        <div>
          <p className="text-[10px] text-slate-400 font-semibold flex items-center gap-1"><Briefcase size={10} /> {insp.experience_years} thn · {insp.completed_count} inspeksi</p>
          <p className="text-sm font-extrabold text-blue-600 mt-0.5">{vehicleType ? "" : "mulai "}{rupiah(price)}</p>
        </div>
        <Button size="sm" data-testid={`book-btn-${insp.user_id}`} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg h-9 px-4">Pesan</Button>
      </div>
    </div>
  );
}
